import React from 'react';
import { X, PieChart, DollarSign, Globe, TrendingUp, Calendar } from 'lucide-react';
import { Button } from './Button';
import { UnifiedCTCTable } from './ConceptTable';
import { OverheadAnalysisCard } from './OverheadAnalysisCard';
import { AnnualTotalCard } from './AnnualTotalCard';
import { Resource } from '../types';
import { formatUSD, formatCOP } from '../utils/formatters';
import { useConfig } from '../contexts/ConfigContext';
import styles from './ResourceDetailModal.module.css';

interface ResourceDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    resource: Resource | null;
}

export function ResourceDetailModal({ isOpen, onClose, resource }: ResourceDetailModalProps) {
    const { config } = useConfig();

    if (!isOpen || !resource) return null;

    const result = resource.monthlyCTC;
    const salaryTypeLabel = result.effectiveSalaryType === 'integral' ? 'Salario Integral' : 'Salario Ordinario';

    // Prepare concepts for the table
    const base = result.concepts.filter(c => c.category === 'base');
    const prestaciones = result.concepts.filter(c => c.category === 'prestaciones');
    const seguridad = result.concepts.filter(c => c.category === 'seguridad');
    const parafiscales = result.concepts.filter(c => c.category === 'parafiscales');
    const beneficios = result.concepts.filter(c => c.category === 'beneficios');
    const costOfLaw = result.concepts.filter(c => c.category === 'costOfLaw');
    const operativos = result.concepts.filter(c => c.category === 'operativos');
    const subtotalCost = result.concepts.filter(c => c.category === 'subtotalCost');
    const adicionales = result.concepts.filter(c => c.category === 'adicionales');
    const totalCost = result.concepts.filter(c => c.category === 'totalCost');

    // Recalculate monthly projection for the AnnualCard logic (similar to CalculatorView)
    const monthlyProjection = Array.from({ length: 12 }, (_, i) => {
        const month = i + 1;
        let monthlyCostUSD = result.totalUSD;
        let monthlyCostCOP = result.totalCOP;

        if (month > 1) {
            const concepts = result.concepts;
            // Subtract one-time costs logic
            const oneTimeUSD = concepts.filter(c => c.name === 'Hiring' || c.name === 'Safety Study').reduce((sum, c) => sum + c.amountUSD, 0);
            const oneTimeCOP = concepts.filter(c => c.name === 'Hiring' || c.name === 'Safety Study').reduce((sum, c) => sum + c.amountCOP, 0);

            if (oneTimeUSD > 0) {
                const financingRate = concepts.find(c => c.name === 'Financing')?.percentage || 0;
                const gaRate = concepts.find(c => c.name === 'G&A Colombia')?.percentage || 0;
                const managementRate = concepts.find(c => c.name === 'Management Fees')?.percentage || 0;
                const totalMarkup = 1 + financingRate + gaRate + managementRate;

                monthlyCostUSD -= (oneTimeUSD * totalMarkup);
                monthlyCostCOP -= (oneTimeCOP * totalMarkup);
            }
        }
        return { month, totalUSD: monthlyCostUSD, totalCOP: monthlyCostCOP };
    });

    const totalAnualUSD = monthlyProjection.reduce((sum, m) => sum + m.totalUSD, 0);
    const totalAnualCOP = monthlyProjection.reduce((sum, m) => sum + m.totalCOP, 0);

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    <h3 className={styles.title}>
                        <PieChart size={20} />
                        Detalle de Costos: <span className={styles.resourceName}>{resource.name}</span>
                    </h3>
                    <button className={styles.closeButton} onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className={styles.content}>
                    <div className={styles.mainContentLayout}>
                        {/* Left Column - Detailed Breakdown */}
                        <div className={styles.leftColumn}>
                            <div className={styles.totalSection}>
                                <div className={`${styles.totalCard} ${styles.totalCardCOP}`}>
                                    <div className={styles.cardBlur}></div>
                                    <div className={styles.cardIconBg}><DollarSign size={128} /></div>
                                    <div className={styles.cardContent}>
                                        <div className={styles.cardHeader}>
                                            <PieChart size={16} className={styles.cardHeaderIcon} />
                                            <h3 className={styles.cardTitle}>Total Monthly Cost - {salaryTypeLabel}</h3>
                                        </div>
                                        <div className={styles.cardAmount}>
                                            {formatCOP(result.totalCOP)}
                                            <span className={styles.cardCurrency}>COP</span>
                                        </div>
                                    </div>
                                </div>

                                <div className={`${styles.totalCard} ${styles.totalCardUSD}`}>
                                    <div className={styles.cardBlur}></div>
                                    <div className={styles.cardIconBg}><Globe size={128} /></div>
                                    <div className={styles.cardContent}>
                                        <div className={styles.cardHeader}>
                                            <TrendingUp size={16} className={styles.cardHeaderIcon} />
                                            <h3 className={styles.cardTitle}>Total Monthly Cost - {salaryTypeLabel}</h3>
                                        </div>
                                        <div className={styles.cardAmount}>
                                            {formatUSD(result.totalUSD)}
                                            <span className={styles.cardCurrency}>USD</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.breakdown}>
                                <UnifiedCTCTable
                                    sections={[
                                        { title: 'Salario Base', concepts: base },
                                        { title: 'Prestaciones Sociales', concepts: prestaciones },
                                        { title: 'Seguridad Social', concepts: seguridad },
                                        { title: 'Parafiscales', concepts: parafiscales },
                                        { title: 'Otros Beneficios', concepts: beneficios },
                                        { title: '', concepts: costOfLaw },
                                        { title: 'Costos Operativos', concepts: operativos },
                                        { title: '', concepts: subtotalCost },
                                        { title: 'Costos Adicionales', concepts: adicionales },
                                        { title: '', concepts: totalCost },
                                    ]}
                                    currentSalaryType={result.effectiveSalaryType}
                                />
                            </div>
                        </div>

                        {/* Right Column - Projections/Analysis matching CalculatorView */}
                        <div className={styles.rightColumn}>
                            <OverheadAnalysisCard
                                salaryType={result.effectiveSalaryType}
                                smmlv={config.smmlv}
                                gaPercentage={config.gaPercentage}
                                baseSalary={result.salaryInfo.baseSalary}
                            />
                            <AnnualTotalCard
                                totalAnualUSD={totalAnualUSD}
                                totalAnualCOP={totalAnualCOP}
                            />

                            <div className={styles.projectionPanel}>
                                <div className={styles.projectionHeader}>
                                    <Calendar size={18} />
                                    <h3>Proyección por Año</h3>
                                </div>
                                <div className={styles.projectionTable}>
                                    <div className={styles.projectionTableHeader}>
                                        <span>Mes</span>
                                        <span>COS</span>
                                        <span>G&A</span>
                                        <span>Total</span>
                                    </div>
                                    {monthlyProjection.map((month) => {
                                        const cos = month.totalUSD / (1 + config.gaPercentage / 100);
                                        const ga = month.totalUSD - cos;
                                        return (
                                            <div key={month.month} className={styles.projectionRow}>
                                                <span className={styles.projectionMonth}>{month.month}</span>
                                                <span className={styles.projectionRowValue}>
                                                    {cos.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
                                                </span>
                                                <span className={styles.projectionRowValue}>
                                                    {ga.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
                                                </span>
                                                <span className={styles.projectionRowTotal}>
                                                    {month.totalUSD.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
